import React, { Suspense, lazy } from 'react';
import { Lock, Plane, Loader2, MapPin, ShieldCheck, Globe2, ArrowLeft, Crosshair, Warehouse } from 'lucide-react';
import { fmt, portal, countryImg } from './FlightScenes';
import { hasWebGL } from './FlightSequence';

const FlightWorld = lazy(() => import('./sim/FlightWorld'));

const REGION = { colombia: 'South America', peru: 'South America', mexico: 'North America' };
const WEATHER = {
  medellin: ['Overcast & Humid 24°C', 'Mountain valley mist, high-rise luxury towers, covert courier lines.'],
  bogota: ['Cold Drizzle 14°C', 'High-altitude capital fog. Embassies, banks and men in uniform.'],
  cali: ['Hot & Clear 29°C', 'Salsa clubs, sugarcane fields and quiet money in old families.'],
  lima: ['Grey Garúa 18°C', 'Pacific fog over the port. Containers leave every hour.'],
  cusco: ['Thin Air 11°C', 'Andes stone streets, tourist cover and mountain trails.'],
  tijuana: ['Dry Heat 27°C', 'Border town glare. Tunnels under the fence, eyes on every corner.'],
  sinaloa: ['Scorching 34°C', 'Sierra ranches and private airstrips. The plaza owns everything.'],
  mexico_city: ['Smog Haze 21°C', 'Twenty million people. Easy to disappear, hard to stay clean.'],
};
export const riskOf = (dest, heat) => {
  const s = dest.risk_base + (heat || 0) / 4;
  if (s >= 45) return ['EXTREME RISK', 'text-[#F87171]'];
  if (s >= 35) return ['HIGH RISK', 'text-[#FB923C]'];
  if (s >= 22) return ['MEDIUM RISK', 'text-[#F3D36B]'];
  return ['LOW RISK', 'text-[#4ADE80]'];
};

export function HubStage({ level, paused, livery, onHangar }) {
  return (
    <div data-testid="flight-hub-3d" className="relative h-[52vh] min-h-[340px] rounded-2xl overflow-hidden border border-white/10 bg-[#04060b]">
      {hasWebGL() && !paused ? (
        <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center text-white/40"><Loader2 className="animate-spin" /></div>}>
          <FlightWorld phase="hub" from="brooklyn" livery={livery} />
        </Suspense>
      ) : <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/flights/terminal.jpg')" }} />}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />
      <div className="absolute left-3 right-3 top-3 sm:left-5 sm:right-auto sm:top-5 sm:max-w-xl rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <p className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold tracking-[0.25em] text-white"><span className="h-2.5 w-2.5 rounded-full bg-[#4ADE80] shadow-[0_0_10px_#4ADE80]" /> BROOKLYN INTERNATIONAL TERMINAL</p>
          <span data-testid="flight-clearance" className="font-mono text-[10px] tracking-widest text-white/50 whitespace-nowrap">CLEARANCE: LEVEL {level}</span>
        </div>
        <p className="mt-1.5 text-xs sm:text-sm text-white/70">Select an international destination to chart a route, check the heat on every district, and open high-stakes distribution contracts.</p>
      </div>
      <button type="button" data-testid="flight-hangar-btn" onClick={onHangar}
        className="absolute right-3 bottom-16 sm:right-5 sm:top-5 sm:bottom-auto h-10 px-4 rounded-full border border-[#C9A84C]/60 bg-black/60 backdrop-blur-md text-[#F3D36B] text-xs font-semibold tracking-[0.2em] flex items-center gap-2 hover:bg-[#C9A84C]/15 transition-colors">
        <Warehouse size={15} /> HANGAR
      </button>
    </div>
  );
}

function DestCard({ d, on, onSelect }) {
  return (
    <button type="button" data-testid={`flight-dest-${d.id}`} onClick={() => onSelect(d)}
      className={`group relative overflow-hidden rounded-xl border p-3 text-left transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 ${on ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-white/10 bg-white/[0.03] hover:border-white/30'}`}>
      <div className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-20 transition-opacity" style={{ backgroundImage: `url('${countryImg(d.country)}')` }} />
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase">{d.country}</span>
        {d.has_safe_house ? <ShieldCheck size={14} className="text-[#4ADE80]" /> : <MapPin size={14} className="text-[#C9A84C]" />}
      </div>
      <div className="relative mt-1 text-base sm:text-lg font-semibold text-white">{d.name.split(',')[0]}</div>
      <div className="relative font-mono text-xs text-[#4ADE80]">{fmt(d.flight_cost)}</div>
    </button>
  );
}

export function DestinationGrid({ data, selected, onSelect }) {
  const total = data.destinations.length + (data.locked_destinations || []).length;
  return (
    <div data-testid="flight-dest-grid" className="relative z-10 -mt-14 mx-2 sm:mx-5 rounded-2xl border border-white/10 bg-black/75 backdrop-blur-md p-3 sm:p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-[0.25em] text-white/80">AVAILABLE GLOBAL DESTINATIONS ({total} HUBS)</p>
        <p className="hidden sm:block font-mono text-[10px] tracking-widest text-[#C9A84C]">ERA {data.era} ACCESS</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {data.destinations.map((d) => <DestCard key={d.id} d={d} on={selected?.id === d.id} onSelect={onSelect} />)}
        {(data.locked_destinations || []).map((d) => (
          <div key={d.id} data-testid={`flight-dest-locked-${d.id}`} className="relative rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-start justify-between"><span className="font-mono text-[10px] tracking-widest text-white/45 uppercase">{d.country}</span><Lock size={13} className="text-white/50" /></div>
            <div className="mt-1 text-base sm:text-lg font-semibold text-white/70">{d.name.split(',')[0]}</div>
            <div className="font-mono text-xs text-[#F87171]/90">ERA {d.min_era}+</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HubHint({ allLocked, minEra }) {
  if (allLocked) return <p data-testid="flight-era-lock-hint" className="text-sm text-[#C9A84C] flex items-center gap-2"><Lock size={14} /> International routes open in Era {minEra}. Keep building in Brooklyn.</p>;
  return <p data-testid="flight-pick-hint" className="text-sm text-white/50 flex items-center gap-2"><MapPin size={14} /> Pick a destination to open its flight dossier.</p>;
}

const Panel = ({ title, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5"><p className="text-[10px] tracking-[0.2em] text-white/45 uppercase">{title}</p>{children}</div>
);

export function FlightDossier({ dest, data, busy, error, onBook, onClose }) {
  const [risk, riskCls] = riskOf(dest, data.heat);
  const [wx, wxDesc] = WEATHER[dest.id] || ['Clear', dest.desc];
  return portal(
    <div className="fixed inset-0 z-[2800] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3" onClick={onClose}>
      <div data-testid="flight-booking-panel" onClick={(e) => e.stopPropagation()} className="fl-rise w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0b0d] p-4 sm:p-6 space-y-4">
        <div className="flex items-start gap-3 border-b border-white/10 pb-4">
          <div className="h-11 w-11 shrink-0 rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/10 flex items-center justify-center"><Globe2 size={20} className="text-[#C9A84C]" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-2xl font-bold tracking-wide text-white uppercase">Flight Dossier: {dest.name.split(',')[0]}</h3>
            <p className="text-sm text-white/55 capitalize">{dest.country} · {REGION[dest.country]}</p>
          </div>
          <button type="button" data-testid="flight-dossier-close" onClick={onClose} className="p-2 text-white/60 hover:text-white transition-colors"><ArrowLeft size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Panel title="Atmosphere & Weather"><p className="mt-1 font-semibold text-white">{wx}</p><p className="mt-1 text-xs italic text-white/55">{wxDesc}</p></Panel>
          <Panel title="Security Risk Index">
            <p data-testid="flight-dossier-risk" className={`mt-1 font-bold ${riskCls}`}>{risk}</p>
            <p className="mt-1 text-xs text-white/60">Base risk {dest.risk_base}% · Your heat {data.heat}%</p>
            <p className="text-xs text-white/60">Req. Era: <span className="text-white">{dest.min_era}</span> · Visited {dest.times_visited}x</p>
            <p data-testid="flight-dossier-jet" className="text-xs text-white/60">Jet: <span className="text-white">{(data.jets || []).find((j) => j.active)?.name}</span>{(() => { const j = (data.jets || []).find((x) => x.active); return j && j.customs_cut ? <span className="text-[#4ADE80]"> · customs -{j.customs_cut} · intercept -{j.intercept_cut}{j.ticket_discount ? ` · tickets -${j.ticket_discount}%` : ''}</span> : null; })()}</p>
            <p data-testid="flight-dossier-safehouse" className="text-xs text-white/60">Safehouse: {dest.has_safe_house ? <span className="text-[#4ADE80]">{dest.safe_house_name || 'Established'} · T{dest.safe_house_tier}</span> : <span className="text-[#C9A84C]">Not Established</span>}</p>
          </Panel>
        </div>
        <Panel title="Active contracts available at destination:">
          <div className="mt-2 space-y-1.5">
            {dest.missions.map((m) => (
              <div key={m.id} data-testid={`flight-dossier-contract-${m.id}`} className={`flex items-center gap-2 rounded-lg border border-white/5 bg-black/40 px-3 py-2 ${m.level_ok ? '' : 'opacity-50'}`}>
                {m.level_ok ? <Crosshair size={14} className="text-[#C9A84C] shrink-0" /> : <Lock size={14} className="text-white/50 shrink-0" />}
                <span className="flex-1 min-w-0 truncate text-sm font-semibold text-white">{m.name}</span>
                <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-white/55">Lvl {m.min_level}</span>
                <span className="font-mono text-xs sm:text-sm text-[#4ADE80]">{fmt(m.estimated_revenue)}</span>
                <span className="hidden sm:inline font-mono text-[10px] text-white/45">+{m.xp} XP</span>
              </div>
            ))}
          </div>
        </Panel>
        {error && <p data-testid="flight-dossier-error" className="rounded-lg border border-[#F87171]/50 bg-[#7f1d1d]/40 px-3 py-2 text-xs text-[#FECACA]">{error}</p>}
        <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
          <p className="flex-1 font-mono text-sm text-white/50">TICKET COST: <b className="text-[#4ADE80]">{fmt(dest.flight_cost)}</b></p>
          <button type="button" data-testid="flight-dossier-cancel" onClick={onClose} className="h-11 px-5 rounded-xl bg-white/5 text-white/80 text-sm hover:bg-white/10 transition-colors">Cancel</button>
          <button type="button" data-testid="flight-book-btn" disabled={busy || !dest.can_afford} onClick={() => onBook(dest)}
            className="h-11 px-5 rounded-xl bg-gradient-to-b from-[#F3D36B] to-[#C9A84C] text-black text-sm font-bold tracking-wider flex items-center gap-2 shadow-[0_0_24px_rgba(201,168,76,0.35)] disabled:opacity-40 transition-opacity">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plane size={16} />} {dest.can_afford ? 'CONFIRM & BOARD FLIGHT' : `NEED ${fmt(dest.flight_cost)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
